import { getAppName, getForceplateAsymGroup, getMetricDropdownLabel, getTestingAsymGroup } from '../../Utils/chart_utils';
import moment from 'moment';
import {trainingAPI} from '../../api/TrainingAPI';
import { chartExportableOptions, getSuffixByStat } from "../mapperUtil"

export function asymChartMapper(
  data, 
  metric, 
  teamColors, 
  stat, 
  showTitle = true, 
  showHamburger = true, 
  enableDrillDown = false,
  session = [], 
  playerGroup = [],
  position = [],
  selectedPlayerId = null
) {
  if (!data || !metric) return;
  const barColors = [teamColors.primary, teamColors.secondary, teamColors.tertiary];

  const restructureData = (original) => {
    const [app, name] = getAppName(metric.name);

    const group = 
      metric.metric_type === "forceplate"
        ? getForceplateAsymGroup(metric.name)
        : metric.metric_type === "testing"
        ? [
            null,
            getTestingAsymGroup(metric?.name),
          ]
        : [];

    const [left, right, asym] = group[1] || [];

    const prefix = app ? app + " - " : "";
    const suffix = getSuffixByStat(metric, stat); 
    const leftValue =
      original[`${prefix}${left}${suffix}`] || original[`${prefix}${left}`] || original[left] || 0;
    const rightValue =
      original[`${prefix}${right}${suffix}`] || original[`${prefix}${right}`] || original[right] || 0;
    const asymValue =
      original[`${prefix}${asym}${suffix}`] || original[`${prefix}${asym}`] || original[asym] || 0;
  

    return {
      date: original.date,
      disp_date: moment(original.date).format("MM-DD"),
      Left: +leftValue.toFixed(2),
      Right: +rightValue.toFixed(2),
      Asym: +asymValue.toFixed(2),
    };
  };

  const chartData = data.map(restructureData);
  const categories = chartData.map(item => item.disp_date)
  const leftSeries = chartData.map((item, index) => {
    return {
      name: categories[index],
      y: item.Left,
      color: barColors[0],
      ...(enableDrillDown && {drilldown: `drilldown-left-${index}`}),
    }
  })

  const rightSeries = chartData.map((item, index) => {
    return {
      name: categories[index],
      y: item.Right,
      color: barColors[1],
      ...(enableDrillDown && {drilldown: `drilldown-right-${index}`}),
    }
  })

  const asymSeries = chartData.map((item, index) => {
    return {
      name: categories[index],
      y: item.Asym,
      color: '#000000',
      ...(enableDrillDown && {drilldown: `drilldown-asymmetry-${index}`}),
    }
  })
  
  const series = [
    {
      name: 'Left',
      type: 'column',
      yAxis: 0,
      data: leftSeries,
      // data: chartData.map(item => item.Left),
      color: barColors[0],
      dataLabels: {
        enabled: true,    
        allowOverlap: true,
        rotation: 270,
        style: {
          textOutline: 'none',
          fontWeight: 'bold',
          fontSize: '12px',
          color: '#ffffff'
        },
        inside: true,
        verticalAlign: 'middle'
      }
    },
    {
      name: 'Right',
      type: 'column',
      yAxis: 0,
      data: rightSeries,
      // data: chartData.map(item => item.Right),
      color: barColors[1],
      dataLabels: {
        enabled: true,
        allowOverlap: true,
        rotation: 270,
        style: {
          textOutline: 'none',
          fontWeight: 'bold',
          fontSize: '12px',
          color: '#ffffff' 
        },
        inside: true,
        verticalAlign: 'middle'
      }
    },
    {
      name: 'Asymmetry',
      type: 'spline',
      yAxis: 1,
      data: asymSeries,
      // data: chartData.map(item => item.Asym),
      color: '#000000',
      marker: {
        lineWidth: 2,
        lineColor: '#000000',
        fillColor: '#FFFFFF'
      },
      dataLabels: {
        enabled: true,
        style: {
          textOutline: 'none',
          fontWeight: 'normal',
          fontSize: '11px',
        },
        backgroundColor: 'white',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: 'black',
        color: 'black',
        padding: 5,
        shape: 'circle',
        verticalAlign: 'middle',
        align: 'center',
        y: 0,
        x: 0,
        useHTML: true,
        formatter: function() {
          return '<div style="width:24px;height:24px;line-height:24px;text-align:center;">' + this.y + '</div>';
        }
      }
    }
  ];

  const title = (showTitle ? getMetricDropdownLabel(metric, false) : '');
  let positionsArray = [];
  let playerNamesArray = [];
  let categoriesArray = [];
  let lastProcessedDrillUpLevel = null;

  const options = {
    chart: {
      type: 'column',
      events: {
        load: function() {
          try {
            this.isDrilling = false;
            this.originalSeries = series || [];
          } catch (error) {
            console.error('Error storing original series:', error);
            this.originalSeries = [];
          }
          this.options.custom = this.options.custom || {};
          this.options.custom.title = showTitle ? title : '';
        },
        drilldown: function(e) {
          if (this.isDrilling) return;
          this.isDrilling = true;

          if (!e.seriesOptions) {
            const chart = this;
            const date = data[e.point.index]?.date;
            if (!chart.drilldownLevel) {
              chart.drilldownLevel = 0;
            }

            if (chart.drilldownLevel >= 3) {
              return; 
            }
            
            const levelNum = chart.drilldownLevel + 1;
            this.options.custom = this.options.custom || {};

            if (!chart.drilldownData) { 
              chart.showLoading('Loading data ...');
              categoriesArray = [...categories]; 
              trainingAPI.getDrillDownChartData({ 
                date: date,
                position,
                player_group: playerGroup,
                sessions: session,
                drilldown_type: metric.metric_type,
                metric_id: metric.metric_id,
                player_id: selectedPlayerId || null
              })
                .then(response => {
                  const data = response.player_data;
                  const metrics = response.metrics;
                  chart.drilldownData = data;

                  let leftMetricName = '', rightMetricName = '', asymMetricName = '';
                  let leftMetric, rightMetric, asymMetric;
                  for(let i = 0; i < metrics.length; i++) {
                    const metric_name = metrics[i].name;
                    if (metric_name.includes("Asym") || metric_name.includes("L|R")) {
                      asymMetricName = metric_name;
                      asymMetric = metrics[i];
                    } else if (metric_name.includes("Left")) {
                      leftMetricName = metric_name;
                      leftMetric = metrics[i];
                    } else if (metric_name.includes("Right")) {
                      rightMetricName = metric_name;
                      rightMetric = metrics[i];
                    }
                  }
                  const index = e.point.index;
                  const metricNameList = [leftMetricName, rightMetricName, asymMetricName];
                  const metricsArr = [leftMetric, rightMetric, asymMetric];
                  const { drilldowns, positions } = createDrilldownStructure(data, metricsArr, metricNameList, index, e.point.name, 
                                                                              enableDrillDown, levelNum, stat);
                  positionsArray = [...positions];
                  const xAxis = chart.xAxis[0];
                  xAxis.categories = positions;
                  this.options.custom.currentCategories = positions;
                  this.options.custom.drilldown = `${e.point.name}_positional_${stat}`;
                  this.options.custom.metric_names = metricNameList;
                  this.options.custom.metricsArr = metricsArr;
                  chart.hideLoading();
                  updateDrilldownSeriesAndYAxisMax(e.point, chart, levelNum, drilldowns, index);
                })
                .catch(error => {
                  console.error('Error fetching drill-down data:', error);
                  chart.hideLoading();
                })
                .finally(() => {
                  this.isDrilling = false;
                })
            } 
            else {
              if (chart.drilldownLevel === 1 ) {
                try{
                  const index = e.point.index;
                  const {drilldowns, names} = createDrilldownStructure(chart.drilldownData,this.options.custom.metricsArr, this.options.custom.metric_names, 
                                                                      index, e.point.name, enableDrillDown, levelNum, stat);
                                                                        
                  const xAxis = chart.xAxis[0];
                  playerNamesArray = [...names];
                  xAxis.categories = names
                  this.options.custom.currentCategories = xAxis.categories;
                  this.options.custom.drilldown = `${e.point.name}_player_${stat}`;
                  updateDrilldownSeriesAndYAxisMax(e.point, chart, levelNum, drilldowns, index);
                }
                catch(error){
                  console.error('Error occurred while setting drill-down level 2 data:', error);
                  chart.hideLoading();
                }
                finally{
                  this.isDrilling = false;
                }
              } else if (chart.drilldownLevel === 2 ) {
                try{
                  const index = e.point.index;
                  const {drilldowns, repNames} = createDrilldownStructure(chart.drilldownData, this.options.custom.metricsArr, this.options.custom.metric_names, 
                                                                      index, e.point.name, enableDrillDown, levelNum, stat);
                                                  
                  const xAxis = chart.xAxis[0];
                  xAxis.categories = repNames
                  this.options.custom.currentCategories = xAxis.categories;
                  this.options.custom.drilldown = `${e.point.name}_playerReps`;
                  updateDrilldownSeriesAndYAxisMax(e.point, chart, levelNum, drilldowns, index);
                }
                catch(error){
                  console.error(`Error occurred while setting drill-down level ${chart.drilldownLevel} data:`, error);
                  chart.hideLoading();
                }
                finally{
                  this.isDrilling = false;
                }
              }
            }
            chart.drilldownLevel++;
            lastProcessedDrillUpLevel = chart.drilldownLevel;
          }
        },
        drillup: function(e) {
          const chart = this;
          const levelNum = e.seriesOptions._levelNumber;

          // Prevent duplicate drillup processing
          if (levelNum === lastProcessedDrillUpLevel || chart.isDrilling) return;

          lastProcessedDrillUpLevel = levelNum;
          chart.isDrilling = true;

          this.options.custom = this.options.custom || {};
          chart.drilldownLevel--;
          try{
            if (levelNum === 2) { 
              const xAxis = chart.xAxis[0];
              xAxis.categories = playerNamesArray;
              this.options.custom.currentCategories = playerNamesArray;
              this.options.custom.drilldown = `${e.seriesOptions.id}_player_${stat}`;
            }
            else if (levelNum === 1) { 
              const xAxis = chart.xAxis[0];
              xAxis.categories = positionsArray;
              this.options.custom.currentCategories = positionsArray;
              this.options.custom.drilldown = `${e.seriesOptions.id}_positional_${stat}`;

            } else if (levelNum === 0) {
              const chart = this;
              if (!chart) return;
              if (chart.drilldownData) {
                delete chart.drilldownData;
              }

              const xAxis = chart.xAxis?.[0];
              if (xAxis && Array.isArray(categories)) {
                xAxis.categories = categories;
                this.options.custom.currentCategories = categories;
                this.options.custom.drilldown = "";
              }
            }

            chart.yAxis[0].setExtremes(null, chart.yAxis[0].getExtremes().dataMax * 1.1);
            chart.yAxis[1].setExtremes(null, chart.yAxis[1].getExtremes().dataMax * 1.1);
          }
          catch(error){
            console.error(`Error occurred while drilling upwards for level ${levelNum} data:`, error);
            chart.hideLoading();
          } finally {
            this.isDrilling = false;
          }
          
        }
      }
    },
    title: {
      text: title
    },
    xAxis: {
      categories: categories,
      labels: categories.length > 20
      ? {
          rotation: -90,
          overflow: 'justify',
          staggerLines: 1,
          step: 0
        }
      : categories.length > 10
      ? {
          rotation: -45,
          overflow: 'justify',
          staggerLines: 1,
          step: 0
        }
      : {
          rotation: 0,
        },
      tickmarkPlacement: categories.length > 10 ? 'on' : 'between',
      crosshair: true,
      gridLineWidth: 1,
      gridLineColor: '#e6e6e6'
    },
    yAxis: [
      {
        title: {
          text: null
        },
        labels: {
          format: '{value}'
        },
      },
      {
        title: {
          text: null
        },
        labels: {
          format: '{value}'
        },
        opposite: true,
      }
    ],
    tooltip: {
      shared: true
    },
    legend: {
      enabled: true,
      align: 'center',
      verticalAlign: 'top',
    },
    plotOptions: {
      column: {
        grouping: true,
        shadow: false,
        borderWidth: 0,
        dataLabels: {
          enabled: true,
          y: categories.length > 20 ? 10 : 0,
          rotation: categories.length > 20 ? 270 : 0,
          inside: true
        }
      }
    },
    drilldown: {
      activeAxisLabelStyle: {
        cursor: 'default',
        color: 'rgb(51, 51, 51)',
        textDecoration: 'none',
        fontWeight: 500,
        tickmarkPlacement: categories.length > 10 ? 'on' : 'between',
        tickWidth: 1
      },
      activeDataLabelStyle: {
        cursor: 'default',
        color: 'rgb(51, 51, 51)',
        textDecoration: 'none',
      },
      series: []  // Leave empty for dynamic loading
    },
    series: series,
    exporting: {
      enabled: showHamburger,
      ...chartExportableOptions
    }
  };

  function createDrilldownStructure(data, metrics, metric_names, index, selectedPointName, enableDrillDown = false, level = 1, stat = 'Avg') {
    const drilldowns = {};
    const positions = [...new Set(data.map(item => item.player_id__position))];
    const seriesNames = ['Left', 'Right', 'Asymmetry']

    if (level == 1) {
      // First layer: Positions
      metric_names.map((metric_name, metric_idx) => {
        const metric = metrics[metric_idx];
        const decimal_places = metric?.decimal_places || 2;
        const suffix = getSuffixByStat(metric, stat);
        const series_name = seriesNames[metric_idx];
        drilldowns[`drilldown-${series_name.toLowerCase()}-${index}`] = {
          name: `Position-${series_name}`,
          id: selectedPointName,
          color: (metric_idx < 2 ? barColors[metric_idx] : '#000000'),
          type: (metric_idx < 2 ? 'column' : 'spline'),
          yAxis: (metric_idx < 2 ? 0 : 1),
          data: positions.map(position => {
            const positionData = data.filter(item => item.player_id__position === position);
            let yVals;
            if (suffix.endsWith('avg')) {
              yVals = positionData.reduce((sum, item) => sum + item[`${metric_name}${suffix}`], 0) / positionData.length;
            } else if (suffix.endsWith('max')) {
              yVals = positionData.reduce((max, item) => Math.max(max, item[`${metric_name}${suffix}`]), -Infinity);
            } else if (suffix.endsWith('min')) {
              yVals = positionData.reduce((min, item) => Math.min(min, item[`${metric_name}${suffix}`]), Infinity);
            } else if (suffix.endsWith('sum')) {
              yVals = positionData.reduce((sum, item) => sum + item[`${metric_name}${suffix}`], 0);
            }

            return {
              name: position,
              y: Number(yVals?.toFixed(decimal_places)),
              ...(enableDrillDown && {drilldown: `${position}-players-${series_name.toLowerCase()}-${index}`})
            };
          }),
          ...(metric_idx == 2 && {
            marker: {
              lineWidth: 2,
              lineColor: '#000000',
              fillColor: '#FFFFFF'
            },
            dataLabels: {
              enabled: true,
              style: {
                textOutline: 'none',
                fontWeight: 'normal',
                fontSize: '11px',
              },
              backgroundColor: 'white',
              borderRadius: 12,
              borderWidth: 1,
              borderColor: 'black',
              color: 'black',
              padding: 5,
              shape: 'circle',
              verticalAlign: 'middle',
              align: 'center',
              y: 0,
              x: 0,
              useHTML: true,
              formatter: function() {
                return '<div style="width:24px;height:24px;line-height:24px;text-align:center;">' + this.y + '</div>';
              }
            }
          })
        }
      })

      return {drilldowns, positions}
    }
    
    if (level == 2) {
      // Second layer: Players
      let names = new Set();
      metric_names.forEach((metric_name, metric_idx) => {
        const series_name = seriesNames[metric_idx];
        const metric = metrics[metric_idx];
        const decimal_places = metric?.decimal_places || 2;
        const suffix = getSuffixByStat(metric, stat);
        positions.filter(pos => pos === selectedPointName).forEach(position => {
          const positionSeriesName = `${position}-players-${series_name.toLowerCase()}-${index}`;
          const playersData = data
            .filter(item => item.player_id__position === position)
            .map(player => {
              const playerName = `${player.player_id__first_name[0]}. ${player.player_id__last_name}`;
              if (!names.has(playerName)) {
                names.add(playerName);  
              }

              let yVals;
              if (suffix.endsWith('avg')) {
                yVals = player[`${metric_name}${suffix}`];
              } else if (suffix.endsWith('max')) {
                yVals = player.reps[metric_name].reduce((max, item) => Math.max(max, item), -Infinity);
              } else if (suffix.endsWith('min')) {
                yVals = player.reps[metric_name].reduce((min, item) => Math.min(min, item), Infinity);
              } else if (suffix.endsWith('sum')) {
                yVals = player.reps[metric_name].reduce((sum, item) => sum + item, 0);
              }
              
              return {
                name: playerName,
                y: Number(yVals?.toFixed(decimal_places)),
                // y: Number(player[`${metric_name}${suffix}`]?.toFixed(decimal_places)),
                ...(enableDrillDown && {drilldown: `${playerName}-player-reps-${series_name.toLowerCase()}-${index}`})
              };
            });

          drilldowns[positionSeriesName] = {
            name: `Players-${seriesNames[metric_idx]}`,
            id: selectedPointName,
            data: playersData,
            color: (metric_idx < 2 ? barColors[metric_idx] : '#000000'),
            type: (metric_idx < 2 ? 'column' : 'spline'),
            yAxis: (metric_idx < 2 ? 0 : 1),
            ...(metric_idx == 2 && {
              marker: {
                lineWidth: 2,
                lineColor: '#000000',
                fillColor: '#FFFFFF'
              },
              dataLabels: {
                enabled: true,
                style: {
                  textOutline: 'none',
                  fontWeight: 'normal',
                  fontSize: '11px',
                },
                backgroundColor: 'white',
                borderRadius: 12,
                borderWidth: 1,
                borderColor: 'black',
                color: 'black',
                padding: 5,
                shape: 'circle',
                verticalAlign: 'middle',
                align: 'center',
                y: 0,
                x: 0,
                useHTML: true,
                formatter: function() {
                  return '<div style="width:24px;height:24px;line-height:24px;text-align:center;">' + this.y + '</div>';
                }
              }
            })
          };
        });
      });
      return{ drilldowns, names: Array.from(names) };
    }
   
    if (level == 3) {
      // Third layer: Player Reps
      let repNames = new Set();
      metric_names.forEach((metric_name, metric_idx) => {
        const series_name = seriesNames[metric_idx];
        data.filter(dp => {
          const playerName = `${dp.player_id__first_name[0]}. ${dp.player_id__last_name}`;
          if (playerName === selectedPointName) return dp;
        }).forEach(dp => {
          const metric = metrics[metric_idx];
          const decimal_places = metric?.decimal_places || 2;
          const playerName = `${dp.player_id__first_name[0]}. ${dp.player_id__last_name}`;
          const playerSeriesName = `${playerName}-player-reps-${series_name.toLowerCase()}-${index}`
          const metricReps = dp.reps[metric_name];
          const playerRepsData = metricReps.map((repVal, i) => {
              const repName = `Rep-${i + 1}`;
              if (!repNames.has(repName)) {
                repNames.add(repName);  
              }

              return {
                name: repName,
                y: Number(repVal?.toFixed(decimal_places)),
              }; 
            });

          drilldowns[playerSeriesName] = {
            name: `Player-Rep-${seriesNames[metric_idx]}`,
            id: selectedPointName,
            data: playerRepsData,
            color: (metric_idx < 2 ? barColors[metric_idx] : '#000000'),
            type: (metric_idx < 2 ? 'column' : 'spline'),
            yAxis: (metric_idx < 2 ? 0 : 1),
            ...(metric_idx == 2 && {
              marker: {
                lineWidth: 2,
                lineColor: '#000000',
                fillColor: '#FFFFFF'
              },
              dataLabels: {
                enabled: true,
                style: {
                  textOutline: 'none',
                  fontWeight: 'normal',
                  fontSize: '11px',
                },
                backgroundColor: 'white',
                borderRadius: 12,
                borderWidth: 1,
                borderColor: 'black',
                color: 'black',
                padding: 5,
                shape: 'circle',
                verticalAlign: 'middle',
                align: 'center',
                y: 0,
                x: 0,
                useHTML: true,
                formatter: function() {
                  return '<div style="width:24px;height:24px;line-height:24px;text-align:center;">' + this.y + '</div>';
                }
              }
            })
          };
        });
      });
      return{ drilldowns, repNames: Array.from(repNames) };
    }
  }

  function updateDrilldownSeriesAndYAxisMax(point, chart, levelNum, drilldowns, index) {
    let drilldown1, drilldown2, drilldown3, pointName; 

    if (levelNum === 1) {
      drilldown1 = drilldowns[`drilldown-left-${index}`];
      drilldown2 = drilldowns[`drilldown-right-${index}`];
      drilldown3 = drilldowns[`drilldown-asymmetry-${index}`];
    } else if (levelNum === 2) {
      pointName = point.name;
      drilldown1 = drilldowns[`${pointName}-players-left-${index}`];
      drilldown2 = drilldowns[`${pointName}-players-right-${index}`];
      drilldown3 = drilldowns[`${pointName}-players-asymmetry-${index}`];
    } else if (levelNum === 3) {
      pointName = point.name;
      drilldown1 = drilldowns[`${pointName}-player-reps-left-${index}`];
      drilldown2 = drilldowns[`${pointName}-player-reps-right-${index}`];
      drilldown3 = drilldowns[`${pointName}-player-reps-asymmetry-${index}`];
    }

    chart.addSingleSeriesAsDrilldown(point, drilldown1);
    chart.addSingleSeriesAsDrilldown(point, drilldown2);
    chart.addSingleSeriesAsDrilldown(point, drilldown3);
    chart.applyDrilldown();

    updateYAxisMax(chart, [...drilldown1.data, ...drilldown2.data], 0);
    updateYAxisMax(chart, [...drilldown3.data], 1);
  }

  function updateYAxisMax(chart, seriesData, index) {
    const maxValue = Math.max(...seriesData.map(point => point.y));
    const newMax = (maxValue >= 0
                    ? maxValue * 1.1           // increase if positive
                    : maxValue * 0.9);
    chart.yAxis[index].setExtremes(null, newMax);
  }
  

  return options;
}